/**
 * BriefDetailModal
 *
 * Full view of a content brief with editing and refinement capabilities.
 * Includes carousel story arc display and per-card editing.
 */

import React, { useState } from 'react';
import {
    X,
    Edit2,
    Check,
    Sparkles,
    Image,
    Video,
    Layers,
    Loader2,
    Copy,
    RefreshCw,
    ChevronDown,
    ChevronUp,
    Plus,
    Trash2
} from 'lucide-react';
import { updateContentBrief } from '../../services/contentPlanService';
import { refineBrief } from '../../services/bulkBriefService';
import ConfirmationModal from '../ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

const CONTENT_TYPE_ICONS = {
    image: Image,
    video: Video,
    carousel: Layers
};

const STORY_ARC_LABELS = {
    problem_solution: 'Problem \u2192 Solution',
    before_after: 'Before \u2192 After',
    feature_showcase: 'Feature Showcase',
    educational_steps: 'Educational Steps',
    testimonial_journey: 'Testimonial Journey'
};

const CARD_ROLE_LABELS = {
    hook: { label: 'Hook', color: 'var(--error-600)' },
    build: { label: 'Build', color: 'var(--info-600)' },
    proof: { label: 'Proof', color: 'var(--warning-600)' },
    cta: { label: 'CTA', color: 'var(--success-600)' },
    support: { label: 'Support', color: 'var(--color-text-secondary)' }
};

const BriefDetailModal = ({ brief, onClose, onUpdate, onApprove, onUnapprove }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isRefining, setIsRefining] = useState(false);
    const [refineInstructions, setRefineInstructions] = useState('');
    const [editedBrief, setEditedBrief] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expandedCards, setExpandedCards] = useState(() => {
        const cards = (brief?.brief || brief)?.carouselCards || [];
        if (brief?.outputType === 'carousel' && cards.length > 0) {
            const initial = {};
            cards.forEach((_, index) => { initial[index] = true; });
            return initial;
        }
        return {};
    });
    const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);

    const { captureInitialState, showUnsavedModal, setShowUnsavedModal, handleCloseAttempt } = useUnsavedChanges(editedBrief);

    if (!brief) return null;

    const briefData = brief.brief || brief;
    const ContentTypeIcon = CONTENT_TYPE_ICONS[brief.outputType] || Image;
    const isCarousel = brief.outputType === 'carousel';

    const handleStartEdit = () => {
        const briefCopy = { ...briefData };
        if (briefCopy.carouselCards) {
            briefCopy.carouselCards = briefCopy.carouselCards.map(card => ({ ...card }));
        }
        setEditedBrief(briefCopy);
        captureInitialState(briefCopy);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setEditedBrief(null);
        setIsEditing(false);
    };

    const handleAttemptClose = () => {
        if (isEditing && handleCloseAttempt()) {
            return;
        }
        onClose();
    };

    const handleDiscardAndClose = () => {
        handleCancelEdit();
        setShowUnsavedModal(false);
        onClose();
    };

    const handleSaveEdit = async () => {
        if (!editedBrief) return;

        setSaving(true);
        try {
            // Add to refinement history for manual edits
            const updatedHistory = [
                ...(brief.refinementHistory || []),
                {
                    instruction: '[Manual edit]',
                    timestamp: Date.now()
                }
            ];

            await updateContentBrief(brief.id, {
                brief: editedBrief,
                refinementHistory: updatedHistory,
                isUserRefined: true,
                status: 'refined'
            });

            const updatedBrief = {
                ...brief,
                brief: editedBrief,
                refinementHistory: updatedHistory,
                isUserRefined: true,
                status: 'refined'
            };
            onUpdate?.(updatedBrief);
            setIsEditing(false);
            setEditedBrief(null);
        } catch (error) {
            console.error('Failed to save edit:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAndClose = async () => {
        await handleSaveEdit();
        setShowUnsavedModal(false);
        onClose();
    };

    const handleRefine = async () => {
        if (!refineInstructions.trim()) return;

        setIsRefining(true);
        try {
            // Pass the full brief object so refineBrief can access refinementHistory
            const refined = await refineBrief(brief, refineInstructions);

            // refined contains: { ...brief, brief: newContent, refinementHistory, isUserRefined, status }
            await updateContentBrief(brief.id, {
                brief: refined.brief,
                refinementHistory: refined.refinementHistory,
                isUserRefined: true,
                status: 'refined',
                userNotes: refineInstructions
            });

            // Update parent with the full refined brief
            const updatedBrief = {
                ...brief,
                brief: refined.brief,
                refinementHistory: refined.refinementHistory,
                isUserRefined: true,
                status: 'refined'
            };
            onUpdate?.(updatedBrief);
            setRefineInstructions('');
        } catch (error) {
            console.error(`[${new Date().toISOString()}] Failed to refine:`, error);
        } finally {
            setIsRefining(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedBrief(prev => ({ ...prev, [field]: value }));
    };

    const handleDeleteCard = () => {
        if (cardToDelete !== null) {
            const currentData = isEditing ? editedBrief : briefData;
            const carouselCards = currentData.carouselCards || [];
            const updatedCards = carouselCards
                .filter((_, idx) => idx !== cardToDelete)
                .map((card, idx) => ({ ...card, cardPosition: idx + 1 }));
            handleFieldChange('carouselCards', updatedCards);
        }
    };

    const currentData = isEditing ? editedBrief : briefData;
    const carouselCards = currentData?.carouselCards || [];

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.4)',
                zIndex: 10000
            }}
            onClick={(e) => e.target === e.currentTarget && handleAttemptClose()}
        >
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: '540px',
                    maxWidth: '90vw',
                    backgroundColor: 'var(--color-bg-primary)',
                    borderLeft: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                    animation: 'slideInRight 0.3s ease'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        {/* Pillar indicator */}
                        <div style={{
                            width: '4px',
                            height: '32px',
                            borderRadius: '2px',
                            background: brief.pillarColor || '#6366F1'
                        }} />
                        <div>
                            <h3 style={{ margin: 0 }}>Brief #{brief.sequence}</h3>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <span>{brief.pillarName}</span>
                                <span>&middot;</span>
                                <span>{brief.funnelStage}</span>
                                <span>&middot;</span>
                                <ContentTypeIcon size={14} />
                                <span>{brief.outputType}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={handleAttemptClose} className="btn btn-ghost" style={{ padding: 'var(--space-2)' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-4)'
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        {/* Headline */}
                        <BriefField
                            label="Headline"
                            value={currentData.selectedHeadline || currentData.headline}
                            field="selectedHeadline"
                            isEditing={isEditing}
                            onChange={handleFieldChange}
                            highlight
                        />

                        {/* Marketing Angle */}
                        <BriefField
                            label="Marketing Angle"
                            value={currentData.marketingAngle}
                            field="marketingAngle"
                            isEditing={isEditing}
                            onChange={handleFieldChange}
                            multiline
                        />

                        {/* Two Column Layout */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                            <BriefField
                                label="Target Audience"
                                value={currentData.targetAudience}
                                field="targetAudience"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                            <BriefField
                                label="Objective"
                                value={currentData.objective}
                                field="objective"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                            <BriefField
                                label="CTA"
                                value={currentData.cta}
                                field="cta"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                            <BriefField
                                label="Desired Emotion"
                                value={currentData.desiredEmotion}
                                field="desiredEmotion"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                        </div>

                        {/* Paid Content Fields - CTA & Product Page URL */}
                        {brief.contentType === 'paid' && (
                            <div style={{
                                padding: 'var(--space-4)',
                                background: 'var(--color-warning-alpha, rgba(245, 158, 11, 0.1))',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-warning, #F59E0B)'
                            }}>
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: 'var(--color-warning, #F59E0B)',
                                    marginBottom: 'var(--space-3)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Paid Campaign Settings
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                                    <BriefField
                                        label="Product Page URL"
                                        value={currentData.productPageUrl}
                                        field="productPageUrl"
                                        isEditing={isEditing}
                                        onChange={handleFieldChange}
                                        placeholder="https://example.com/product"
                                    />
                                    <BriefField
                                        label="Product"
                                        value={currentData.product}
                                        field="product"
                                        isEditing={isEditing}
                                        onChange={handleFieldChange}
                                    />
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                            <BriefField
                                label="Tone"
                                value={currentData.tone}
                                field="tone"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                            <BriefField
                                label="Visual Style"
                                value={currentData.visualStyle}
                                field="visualStyle"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                        </div>

                        {/* Scene Description - hidden for carousels since each card has its own */}
                        {!isCarousel && (
                            <BriefField
                                label="Scene Description"
                                value={currentData.sceneDescription}
                                field="sceneDescription"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                                multiline
                            />
                        )}

                        {/* Carousel Story Arc Section */}
                        {isCarousel && carouselCards.length > 0 && (
                            <div style={{
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-border)'
                            }}>
                                {/* Story Arc Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    <Layers size={16} style={{ color: 'var(--color-primary)' }} />
                                    <span style={{ fontWeight: 600 }}>Carousel Story</span>
                                    <span style={{
                                        marginLeft: 'auto',
                                        padding: '2px 8px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        background: 'var(--color-primary-alpha)',
                                        color: 'var(--color-primary)',
                                        borderRadius: 'var(--radius-full)'
                                    }}>
                                        {carouselCards.length} cards
                                    </span>
                                </div>

                                {/* Story Arc Type */}
                                {(currentData.storyArcType || isEditing) && (
                                    <div style={{ marginBottom: 'var(--space-3)' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: 500,
                                            color: 'var(--color-text-secondary)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            marginBottom: 'var(--space-1)'
                                        }}>
                                            Story Arc Type
                                        </label>
                                        {isEditing ? (
                                            <select
                                                value={currentData.storyArcType || ''}
                                                onChange={(e) => handleFieldChange('storyArcType', e.target.value)}
                                                className="input"
                                                style={{ width: '100%' }}
                                            >
                                                <option value="">Select arc type...</option>
                                                {Object.entries(STORY_ARC_LABELS).map(([key, label]) => (
                                                    <option key={key} value={key}>{label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div style={{
                                                padding: 'var(--space-2)',
                                                background: 'var(--color-primary-alpha)',
                                                borderRadius: 'var(--radius-md)',
                                                fontSize: 'var(--font-size-sm)',
                                                fontWeight: 500,
                                                color: 'var(--color-primary)'
                                            }}>
                                                {STORY_ARC_LABELS[currentData.storyArcType] || currentData.storyArcType}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Story Narrative */}
                                {(currentData.storyNarrative || isEditing) && (
                                    <div style={{ marginBottom: 'var(--space-3)' }}>
                                        <BriefField
                                            label="Story Narrative"
                                            value={currentData.storyNarrative}
                                            field="storyNarrative"
                                            isEditing={isEditing}
                                            onChange={handleFieldChange}
                                            multiline
                                            placeholder="Overall story that the carousel tells..."
                                        />
                                    </div>
                                )}

                                {/* Individual Cards */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--space-2)'
                                }}>
                                    {carouselCards.map((card, index) => (
                                        <CarouselCardItem
                                            key={index}
                                            card={card}
                                            index={index}
                                            isEditing={isEditing}
                                            isExpanded={expandedCards[index] || false}
                                            onToggleExpand={() => setExpandedCards(prev => ({
                                                ...prev,
                                                [index]: !prev[index]
                                            }))}
                                            onCardChange={(field, value) => {
                                                const updatedCards = [...carouselCards];
                                                updatedCards[index] = { ...updatedCards[index], [field]: value };
                                                handleFieldChange('carouselCards', updatedCards);
                                            }}
                                            onDeleteCard={() => {
                                                setCardToDelete(index);
                                                setShowDeleteCardConfirm(true);
                                            }}
                                        />
                                    ))}
                                </div>

                                {/* Add Card button (editing mode only) */}
                                {isEditing && carouselCards.length < 8 && (
                                    <button
                                        onClick={() => {
                                            const newCard = {
                                                cardPosition: carouselCards.length + 1,
                                                cardRole: 'support',
                                                headline: '',
                                                sceneDescription: ''
                                            };
                                            handleFieldChange('carouselCards', [...carouselCards, newCard]);
                                        }}
                                        className="btn btn-ghost btn-sm"
                                        style={{ marginTop: 'var(--space-2)', width: '100%' }}
                                    >
                                        <Plus size={14} />
                                        Add Card
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Hook Description */}
                        {currentData.hookDescription && (
                            <BriefField
                                label="Hook"
                                value={currentData.hookDescription}
                                field="hookDescription"
                                isEditing={isEditing}
                                onChange={handleFieldChange}
                            />
                        )}

                        {/* Caption / Post Description */}
                        <BriefField
                            label="Caption / Post Description"
                            value={currentData.caption}
                            field="caption"
                            isEditing={isEditing}
                            onChange={handleFieldChange}
                            multiline
                            placeholder="Engaging social media caption for this post..."
                        />

                        {/* Hashtags */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: 'var(--space-1)'
                            }}>
                                Hashtags
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={Array.isArray(currentData.hashtags) ? currentData.hashtags.join(', ') : (currentData.hashtags || '')}
                                    onChange={(e) => handleFieldChange('hashtags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                                    placeholder="hashtag1, hashtag2, hashtag3"
                                    className="input"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--space-1)',
                                    padding: 'var(--space-2)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    minHeight: '36px'
                                }}>
                                    {currentData.hashtags && currentData.hashtags.length > 0 ? (
                                        currentData.hashtags.map((tag, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    padding: 'var(--space-1) var(--space-2)',
                                                    background: 'var(--color-primary-alpha)',
                                                    color: 'var(--color-primary)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    fontWeight: 500
                                                }}
                                            >
                                                #{tag}
                                            </span>
                                        ))
                                    ) : (
                                        <span style={{ fontStyle: 'italic', opacity: 0.5, fontSize: 'var(--font-size-sm)' }}>
                                            No hashtags specified
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* AI Refinement Section */}
                        {!isEditing && (
                            <div style={{
                                padding: 'var(--space-4)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-border)'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    <Sparkles size={16} style={{ color: 'var(--color-primary)' }} />
                                    <span style={{ fontWeight: 500 }}>AI Refinement</span>
                                </div>
                                <textarea
                                    value={refineInstructions}
                                    onChange={(e) => setRefineInstructions(e.target.value)}
                                    placeholder="Tell the AI how to improve this brief... (e.g., 'Make the headline more urgent', 'Focus on social proof')"
                                    className="input"
                                    style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        resize: 'vertical',
                                        marginBottom: 'var(--space-2)'
                                    }}
                                />
                                <button
                                    onClick={handleRefine}
                                    disabled={!refineInstructions.trim() || isRefining}
                                    className="btn btn-primary btn-sm"
                                >
                                    {isRefining ? (
                                        <>
                                            <Loader2 size={14} className="spin" />
                                            Refining...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw size={14} />
                                            Refine with AI
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-secondary)'
                }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {isEditing ? (
                            <>
                                <button onClick={handleCancelEdit} className="btn btn-ghost">
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                    className="btn btn-primary"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 size={14} className="spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Check size={14} />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </>
                        ) : (
                            <button onClick={handleStartEdit} className="btn btn-ghost">
                                <Edit2 size={14} />
                                Edit Manually
                            </button>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        {brief.status !== 'approved' && !isEditing && (
                            <button onClick={onApprove} className="btn btn-primary">
                                <Check size={14} />
                                Approve Brief
                            </button>
                        )}
                        {brief.status === 'approved' && (
                            <button
                                onClick={onUnapprove}
                                className="btn"
                                title="Click to unapprove"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-3)',
                                    background: 'var(--success-100)',
                                    color: 'var(--success-700)',
                                    border: '1px solid var(--success-300)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                <Check size={14} />
                                Approved (click to undo)
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Card Deletion Confirmation */}
            <ConfirmationModal
                isOpen={showDeleteCardConfirm}
                onClose={() => {
                    setShowDeleteCardConfirm(false);
                    setCardToDelete(null);
                }}
                onConfirm={handleDeleteCard}
                title="Remove Carousel Card?"
                message={`Are you sure you want to remove card ${cardToDelete !== null ? cardToDelete + 1 : ''} from this carousel? This cannot be undone.`}
                confirmText="Remove Card"
                cancelText="Keep Card"
                variant="danger"
            />

            {/* Unsaved Changes Confirmation */}
            <ConfirmationModal
                isOpen={showUnsavedModal}
                onClose={() => setShowUnsavedModal(false)}
                onConfirm={handleSaveAndClose}
                onSecondaryAction={handleDiscardAndClose}
                title="Unsaved Changes"
                message="You have unsaved changes to this brief. Would you like to save them before closing?"
                confirmText="Save Changes"
                secondaryActionText="Discard Changes"
                secondaryVariant="danger"
                cancelText="Keep Editing"
                variant="primary"
            />
        </div>
    );
};

// Carousel Card Item Component
const CarouselCardItem = ({ card, index, isEditing, isExpanded, onToggleExpand, onCardChange, onDeleteCard }) => {
    const roleConfig = CARD_ROLE_LABELS[card.cardRole] || CARD_ROLE_LABELS.support;

    return (
        <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden'
        }}>
            {/* Card Header - Always visible */}
            <div
                onClick={onToggleExpand}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: 'var(--color-bg-primary)',
                    cursor: 'pointer'
                }}
            >
                <span style={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-bg-tertiary)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    flexShrink: 0
                }}>
                    {card.cardPosition || index + 1}
                </span>

                <span style={{
                    padding: '1px 6px',
                    fontSize: '9px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: roleConfig.color,
                    background: `color-mix(in srgb, ${roleConfig.color} 10%, transparent)`,
                    borderRadius: 'var(--radius-full)',
                    flexShrink: 0
                }}>
                    {roleConfig.label}
                </span>

                <span style={{
                    flex: 1,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {card.headline || 'Untitled card'}
                </span>

                {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>

            {/* Card Details - Expanded */}
            {isExpanded && (
                <div style={{
                    padding: 'var(--space-3)',
                    borderTop: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)'
                }}>
                    {isEditing && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    color: 'var(--color-text-secondary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: 'var(--space-1)'
                                }}>Role</label>
                                <select
                                    value={card.cardRole || 'support'}
                                    onChange={(e) => onCardChange('cardRole', e.target.value)}
                                    className="input"
                                    style={{ width: '100%' }}
                                >
                                    <option value="hook">Hook</option>
                                    <option value="build">Build</option>
                                    <option value="proof">Proof</option>
                                    <option value="cta">CTA</option>
                                    <option value="support">Support</option>
                                </select>
                            </div>
                            {card.cardRole === 'hook' && (
                                <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 500,
                                        color: 'var(--color-text-secondary)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        marginBottom: 'var(--space-1)'
                                    }}>Hook Type</label>
                                    <select
                                        value={card.hookType || ''}
                                        onChange={(e) => onCardChange('hookType', e.target.value)}
                                        className="input"
                                        style={{ width: '100%' }}
                                    >
                                        <option value="question">Question</option>
                                        <option value="curiosity">Curiosity</option>
                                        <option value="transformation">Transformation</option>
                                        <option value="shock">Shock</option>
                                        <option value="statistic">Statistic</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    <BriefField
                        label="Headline"
                        value={card.headline}
                        field="headline"
                        isEditing={isEditing}
                        onChange={(field, value) => onCardChange(field, value)}
                    />

                    <BriefField
                        label="Scene Description (Image Prompt)"
                        value={card.sceneDescription}
                        field="sceneDescription"
                        isEditing={isEditing}
                        onChange={(field, value) => onCardChange(field, value)}
                        multiline
                        placeholder="Detailed scene description for image generation (50-100 words)..."
                    />

                    {/* Delete card button */}
                    {isEditing && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteCard();
                            }}
                            className="btn btn-ghost btn-sm"
                            style={{
                                color: 'var(--error-600)',
                                alignSelf: 'flex-end'
                            }}
                        >
                            <Trash2 size={14} />
                            Remove Card
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Brief Field Component
const BriefField = ({ label, value, field, isEditing, onChange, multiline, highlight, placeholder }) => (
    <div>
        <label style={{
            display: 'block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 'var(--space-1)'
        }}>
            {label}
        </label>
        {isEditing ? (
            multiline ? (
                <textarea
                    value={value || ''}
                    onChange={(e) => onChange(field, e.target.value)}
                    placeholder={placeholder}
                    className="input"
                    style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                />
            ) : (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(field, e.target.value)}
                    placeholder={placeholder}
                    className="input"
                    style={{ width: '100%' }}
                />
            )
        ) : (
            <div style={{
                padding: 'var(--space-2)',
                background: highlight ? 'var(--color-primary-alpha)' : 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                fontSize: highlight ? 'var(--font-size-base)' : 'var(--font-size-sm)',
                fontWeight: highlight ? 600 : 400,
                color: highlight ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                minHeight: multiline ? '60px' : 'auto',
                whiteSpace: multiline ? 'pre-wrap' : 'normal'
            }}>
                {value || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>Not specified</span>}
            </div>
        )}
    </div>
);

export default BriefDetailModal;
